import React, { useState, useEffect } from 'react';
import { X, Calendar, Users, Euro, Phone, Mail, User, Loader, Clock } from 'lucide-react';
import bookingService from '../services/bookingService';
import eventService from '../services/eventService';
import './AdminCreateBookingModal.css';

export default function AdminCreateBookingModal({ show, onClose, onSuccess, selectedDate }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedTime, setSelectedTime] = useState('10:00');
  
  // Serviços predefinidos com preços da HomePage
  const servicosPredefinidos = [
    { 
      nome: 'Brincoteca - Hora da brincadeira', 
      tipo: 'Brincoteca', 
      duracao: 60, 
      preco: 5,
      precoPorParticipante: false
    },
    { 
      nome: 'Festa de aniversário', 
      tipo: 'FestaAniversario', 
      duracao: 150, 
      preco: 18,
      precoPorParticipante: true  // Preço por participante
    },
    { 
      nome: 'Personalizado', 
      tipo: 'Outro', 
      duracao: 60, 
      preco: 0,
      precoPorParticipante: false
    }
  ];

  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    numeroParticipantes: 1,
    dataHora: selectedDate || new Date(),
    preco: 0,
    observacoes: '',
    isEvent: false,
    eventId: null,
    servicoNome: '',
    servicoTipo: 'Brincoteca',
    duracao: 60,
    eventEndDate: null,
    selectedDays: null
  });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');

  // Atualizar data quando selectedDate muda
  useEffect(() => {
    if (selectedDate) {
      const newDate = new Date(selectedDate);
      const [hours, minutes] = selectedTime.split(':');
      newDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      
      setFormData(prev => ({
        ...prev,
        dataHora: newDate
      }));
    }
  }, [selectedDate, selectedTime]);

  useEffect(() => {
    if (show) loadEvents();
  }, [show]);

  const loadEvents = async () => {
    try {
      const data = await eventService.getEvents();
      setEvents(data.filter(e => e.isActive));
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      // Se mudou o número de participantes e tem serviço selecionado
      if (name === 'numeroParticipantes' && prev.servicoNome) {
        const servico = servicosPredefinidos.find(s => s.nome === prev.servicoNome);
        if (servico && servico.precoPorParticipante) {
          // Recalcular preço
          const numParticipantes = parseInt(value) || 1;
          newData.preco = servico.preco * numParticipantes;
        }
      }
      
      return newData;
    });
    
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleTimeChange = (e) => {
    const time = e.target.value;
    setSelectedTime(time);
    
    const newDate = new Date(selectedDate || formData.dataHora);
    const [hours, minutes] = time.split(':');
    newDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    
    const updates = { dataHora: newDate };
    
    if (formData.isEvent && formData.eventId) {
      updates.selectedDays = JSON.stringify([newDate.toISOString().split('T')[0]]);
    }
    
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleEventSelect = (e) => {
    const eventId = parseInt(e.target.value);
    const event = events.find(ev => ev.id === eventId);
    
    if (event) {
      setFormData(prev => ({
        ...prev,
        eventId,
        isEvent: true,
        preco: event.price || 0,
        servicoNome: event.title,
        selectedDays: JSON.stringify([prev.dataHora.toISOString().split('T')[0]])
      }));
    }
  };

  const handleServicoSelect = (e) => {
    const servicoNome = e.target.value;
    const servico = servicosPredefinidos.find(s => s.nome === servicoNome);
    
    if (servico) {
      // Calcular preço: se for por participante, multiplicar pelo número
      const precoFinal = servico.precoPorParticipante 
        ? servico.preco * (formData.numeroParticipantes || 1)
        : servico.preco;
      
      setFormData(prev => ({
        ...prev,
        servicoNome: servico.nome,
        servicoTipo: servico.tipo,
        duracao: servico.duracao,
        preco: precoFinal
      }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.nome.trim()) newErrors.nome = 'Nome obrigatório';
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = 'Email válido obrigatório';
    if (!formData.telefone.trim() || !/^\d{9}$/.test(formData.telefone.replace(/\s/g, '')))
      newErrors.telefone = 'Telefone válido obrigatório (9 dígitos)';
    if (!formData.numeroParticipantes || formData.numeroParticipantes < 1)
      newErrors.numeroParticipantes = 'Mínimo 1 participante';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    if (!formData.dataHora) newErrors.dataHora = 'Data obrigatória';
    if (!formData.preco || formData.preco < 0) newErrors.preco = 'Preço inválido';
    
    if (!formData.isEvent) {
      if (!formData.servicoNome.trim()) newErrors.servicoNome = 'Selecione um serviço';
      if (!formData.duracao || formData.duracao <= 0) newErrors.duracao = 'Duração inválida';
    } else {
      if (!formData.eventId) newErrors.eventId = 'Selecione um evento';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;

    setLoading(true);
    setMessage('');

    try {
      await bookingService.createBookingForClient({
        nome: formData.nome,
        email: formData.email,
        telefone: formData.telefone,
        numeroParticipantes: parseInt(formData.numeroParticipantes),
        dataHora: formData.dataHora.toISOString(),
        preco: parseFloat(formData.preco),
        observacoes: formData.observacoes,
        isEvent: formData.isEvent,
        eventId: formData.eventId,
        servicoNome: formData.servicoNome,
        servicoTipo: formData.servicoTipo,
        duracao: parseInt(formData.duracao),
        eventEndDate: formData.eventEndDate ? new Date(formData.eventEndDate).toISOString() : null,
        selectedDays: formData.selectedDays
      });

      setMessage('Booking criado com sucesso! Email enviado ao cliente.');
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 2000);
    } catch (error) {
      setMessage(error.message || 'Erro ao criar booking');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedTime('10:00');
    setFormData({
      nome: '',
      email: '',
      telefone: '',
      numeroParticipantes: 1,
      dataHora: selectedDate || new Date(),
      preco: 0,
      observacoes: '',
      isEvent: false,
      eventId: null,
      servicoNome: '',
      servicoTipo: 'Brincoteca',
      duracao: 60,
      eventEndDate: null,
      selectedDays: null
    });
    setErrors({});
    setMessage('');
    onClose();
  };

  const formatDateForDisplay = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('pt-PT', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="admin-booking-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Criar Booking para Cliente</h2>
          <button className="close-btn" onClick={handleClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-progress">
          <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>1. Dados Cliente</div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>2. Dados Booking</div>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {step === 1 && (
            <div className="form-step">
              <div className="form-group">
                <label><User size={18} /> Nome *</label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  className={errors.nome ? 'error' : ''}
                  placeholder="Nome completo do cliente"
                />
                {errors.nome && <span className="error-msg">{errors.nome}</span>}
              </div>

              <div className="form-group">
                <label><Mail size={18} /> Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                  placeholder="email@exemplo.com"
                />
                {errors.email && <span className="error-msg">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label><Phone size={18} /> Telefone *</label>
                <input
                  type="tel"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleChange}
                  placeholder="912345678"
                  className={errors.telefone ? 'error' : ''}
                />
                {errors.telefone && <span className="error-msg">{errors.telefone}</span>}
              </div>

              <div className="form-group">
                <label><Users size={18} /> Nº Participantes *</label>
                <input
                  type="number"
                  name="numeroParticipantes"
                  value={formData.numeroParticipantes}
                  onChange={handleChange}
                  min="1"
                  className={errors.numeroParticipantes ? 'error' : ''}
                />
                {errors.numeroParticipantes && <span className="error-msg">{errors.numeroParticipantes}</span>}
              </div>

              <button type="button" onClick={handleNext} className="btn-next">
                Próximo
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="form-step">
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="isEvent"
                    checked={formData.isEvent}
                    onChange={handleChange}
                  />
                  É reserva de evento?
                </label>
              </div>

              {formData.isEvent ? (
                <div className="form-group">
                  <label>Selecionar Evento *</label>
                  <select
                    name="eventId"
                    value={formData.eventId || ''}
                    onChange={handleEventSelect}
                    className={errors.eventId ? 'error' : ''}
                  >
                    <option value="">Selecione um evento...</option>
                    {events.map(event => (
                      <option key={event.id} value={event.id}>
                        {event.title} - €{event.price?.toFixed(2)}
                      </option>
                    ))}
                  </select>
                  {errors.eventId && <span className="error-msg">{errors.eventId}</span>}
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label>Selecionar Serviço *</label>
                    <select
                      name="servicoSelect"
                      value={formData.servicoNome}
                      onChange={handleServicoSelect}
                      className={errors.servicoNome ? 'error' : ''}
                    >
                      <option value="">Selecione um serviço...</option>
                      {servicosPredefinidos.map((servico, index) => (
                        <option key={index} value={servico.nome}>
                          {servico.nome} 
                          {servico.preco > 0 && (
                            servico.precoPorParticipante 
                              ? ` - €${servico.preco}/participante` 
                              : ` - €${servico.preco}`
                          )}
                        </option>
                      ))}
                    </select>
                    {errors.servicoNome && <span className="error-msg">{errors.servicoNome}</span>}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Tipo</label>
                      <input
                        type="text"
                        value={formData.servicoTipo}
                        disabled
                        style={{ background: '#f3f4f6' }}
                      />
                    </div>

                    <div className="form-group">
                      <label>Duração (min)</label>
                      <input
                        type="number"
                        name="duracao"
                        value={formData.duracao}
                        onChange={handleChange}
                        min="1"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* DATA SELECIONADA (só display) */}
              <div className="form-group date-display">
                <label><Calendar size={18} /> Data Selecionada</label>
                <div className="date-display-value">
                  {formatDateForDisplay(selectedDate || formData.dataHora)}
                </div>
              </div>

              {/* HORA (editável) */}
              <div className="form-group">
                <label><Clock size={18} /> Hora *</label>
                <input
                  type="time"
                  value={selectedTime}
                  onChange={handleTimeChange}
                  className={errors.dataHora ? 'error' : ''}
                />
                {errors.dataHora && <span className="error-msg">{errors.dataHora}</span>}
              </div>

              <div className="form-group">
                <label><Euro size={18} /> Preço Total (€) *</label>
                <input
                  type="number"
                  name="preco"
                  value={formData.preco}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className={errors.preco ? 'error' : ''}
                />
                {errors.preco && <span className="error-msg">{errors.preco}</span>}
              </div>

              <div className="form-group">
                <label>Observações</label>
                <textarea
                  name="observacoes"
                  value={formData.observacoes}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Informações adicionais sobre o booking..."
                />
              </div>

              {message && (
                <div className={`message ${message.includes('sucesso') ? 'success' : 'error'}`}>
                  {message}
                </div>
              )}

              <div className="modal-actions">
                <button type="button" onClick={() => setStep(1)} className="btn-back">
                  Voltar
                </button>
                <button type="submit" disabled={loading} className="btn-submit">
                  {loading ? <Loader className="spinner" size={20} /> : 'Criar Booking'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}